import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ColumnVisibilityIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { DataViewOption } from '@va/types/report-block';
import {
  ColumnVisibilityButton,
  DataTableV8,
  useColumnVisibilityContext,
  useControlledTableState,
} from '@va/ui/components/data-table';
import { ReportBlock } from '@va/ui/components/report-block';
import { ReportBlockNodeIds } from '@va/ui/components/report-block/nodes';
import { sortByMapper } from '@va/ui/design-system';
import { useCallback, useMemo } from 'react';
import { SegmentsBulkOperations } from './SegmentsBulkOperations';
import { useVisitorSegmentsContext } from './VisitorSegmentsContext';
import { useVisitorSegmentsColumns } from './useVisitorSegmentsColumns';

const paginationId = 'segments-table-pagination-container';
const rbId = 'visitor-segments';

const VisitorSegmentsTable = () => {
  const translate = useTranslate();
  const columns = useVisitorSegmentsColumns();
  const { segments, isLoading, totalCount, setTableQuery } = useVisitorSegmentsContext();
  const columnVisibilityNode = useMemo(
    () => ({
      id: ReportBlockNodeIds.COLUMN_VISIBILITY,
      icon: <ColumnVisibilityIcon />,
      label: translate('tooltip.label.showHideTableColumns'),
      headerComponent: <ColumnVisibilityButton />,
      hideCloseButton: true,
      defaultActiveNode: true,
      useIsActive: () => {
        const { isNonDefault } = useColumnVisibilityContext();
        return isNonDefault;
      },
    }),
    [translate],
  );

  const { sorting, setSorting, pagination, setPagination } = useControlledTableState(undefined, rbId);

  const handleSortingChange = useCallback<OnChangeFn<SortingState>>(
    (updater) => {
      if (typeof updater !== 'function') return;

      const updatedSorting = updater(sorting);
      setSorting(updatedSorting);
      setTableQuery((prev: { order: string | undefined }) => ({ ...prev, order: sortByMapper(updatedSorting) }));
    },
    [sorting, setSorting, setTableQuery],
  );

  return (
    <ReportBlock
      id={rbId}
      className='!h-fit w-full xl:w-[60%]'
      border
      nodes={{
        bulkActions: <SegmentsBulkOperations />,
        pagination: <div id={paginationId}></div>,
        columnVisibility: columnVisibilityNode,
      }}
      title={translate('all.visitorSegments.title')}
      visualization={{ selectedView: DataViewOption.table }}
      visualizationComponents={{
        [DataViewOption.table]: () => (
          <DataTableV8
            id='visitor-segments-table'
            columns={columns}
            data={!isLoading ? segments : []}
            isLoading={isLoading}
            rowCount={totalCount}
            manualPagination={false}
            state={{ pagination, sorting }}
            onPaginationChange={setPagination}
            onSortingChange={handleSortingChange}
            paginationContainerId={paginationId}
          />
        ),
      }}
    />
  );
};
export default VisitorSegmentsTable;
